/**
 * Where the org chart's viewport settles (UI-020).
 *
 * Fitting a large tree into the window used to zoom out until it fit — 34 %
 * on the customer's screen, where no name can be read. A fit now stays
 * readable: never below `READABLE_ZOOM_MIN`, and a small tree no larger than
 * life. What does not fit is reached by dragging, from the top of the tree.
 *
 * And a window that changes size keeps its zoom: the point in the middle
 * stays in the middle, so the chart neither jumps nor drifts out of sight.
 */

/** The farthest an automatic fit zooms out — the customer's decision of 2026-11-05. */
export const READABLE_ZOOM_MIN = 0.75;
/** The farthest an automatic fit zooms in: a two-card chart at 200 % is all card. */
export const READABLE_ZOOM_MAX = 1;

export interface ViewportState {
  x: number;
  y: number;
  scale: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The box around every card. */
export function boundsOf(boxes: readonly Box[]): Box | null {
  if (boxes.length === 0) return null;
  const left = Math.min(...boxes.map((box) => box.x));
  const top = Math.min(...boxes.map((box) => box.y));
  const right = Math.max(...boxes.map((box) => box.x + box.width));
  const bottom = Math.max(...boxes.map((box) => box.y + box.height));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

/**
 * The viewport that fits the tree readably.
 *
 * Computed here rather than read back after the library's own fit, whose
 * promise settles before the viewport it set can be read. The fit is the
 * scale at which `bounds` fills the window less `padding` all round —
 * kept between `READABLE_ZOOM_MIN` and `READABLE_ZOOM_MAX`. Within the
 * limits, and when only too close, the tree is centred. Too far: the top of
 * the tree — `top`, the root unit's card — is centred `padding` below the
 * window's edge, and the rest is a drag away.
 */
export function readableFit(
  bounds: Box,
  window: Size,
  top: Box,
  padding: number,
): ViewportState {
  const fit = Math.min(
    (window.width - 2 * padding) / Math.max(bounds.width, 1),
    (window.height - 2 * padding) / Math.max(bounds.height, 1),
  );
  if (fit < READABLE_ZOOM_MIN) {
    return {
      x: window.width / 2 - (top.x + top.width / 2) * READABLE_ZOOM_MIN,
      y: padding - top.y * READABLE_ZOOM_MIN,
      scale: READABLE_ZOOM_MIN,
    };
  }
  const scale = Math.min(fit, READABLE_ZOOM_MAX);
  return {
    x: window.width / 2 - (bounds.x + bounds.width / 2) * scale,
    y: window.height / 2 - (bounds.y + bounds.height / 2) * scale,
    scale,
  };
}

/** The viewport after the window changed size: same zoom, same point in the middle. */
export function recentred(viewport: ViewportState, before: Size, after: Size): ViewportState {
  return {
    x: viewport.x + (after.width - before.width) / 2,
    y: viewport.y + (after.height - before.height) / 2,
    scale: viewport.scale,
  };
}
